import { useState, useEffect } from "react";
import { useCaptureEvent } from "./useCaptureEvent";
import useUpdateQuery from "./useUpdateQuery";
import { paginationConfig } from "../axios/constant";
import { useQuery, useQueryClient } from "react-query";
import { isEmptyObject } from "../utils/GenericFn";
import { useLocation } from "react-router-dom";

const parseQueryParamsFromUrl = () => {
  const params = new URLSearchParams(window.location.search);

  const filters = {};
  const page = parseInt(params.get("page")) || 1;
  const perPage = parseInt(params.get("per_page")) || 20;
  const sort = params.get("sort") || "";

  for (const [key, value] of params.entries()) {
    const match = key.match(/^filter\[(.+)\]$/);
    if (match) {
      filters[match[1]] = {
        selectedScope: match[1],
        value: value.replace(/\|/g, ","),
      };
    }
  }

  return {
    filters,
    pagination: { page, perPage },
    sort,
    hasParams: params.toString().length > 0,
  };
};

export const useTableData = ({
  queryKey,
  fetchFn,
  defaultSort = "",
  defaultFilters = {},
  defaultHeaders = {},
  getCount,
  enabled = true,
  onSuccess: externalSuccess,
  onError: externalError,
}) => {
  const queryClient = useQueryClient();

  const {
    filters: initialFilters,
    pagination: initialPagination,
    sort: initialSort,
    hasParams,
  } = parseQueryParamsFromUrl();

  const location = useLocation();

  const [isHydrated, setIsHydrated] = useState(!hasParams); // true if no params

  // Core data pieces
  const [data, setData] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState({});
  const [filterMetadata, setFilterMetadata] = useState({});
  const [columnMetadata, setColumnMetadata] = useState({});
  const [apiResponse, setApiResponse] = useState(null);

  const [filters, setFilters] = useState(hasParams ? initialFilters : defaultFilters);
  const [pagination, setPagination] = useState(hasParams ? initialPagination : paginationConfig);
  const [sort, setSort] = useState(hasParams ? initialSort : defaultSort);

  const [filterQuery, setFilterQuery] = useState("");
  const [columns, setColumns] = useState({});
  const [sortedVisibleColumns, setSortedVisibleColumns] = useState(null);

  const captureEvent = useCaptureEvent();
  const setQueryString = useUpdateQuery();

  // ✅ Generate a consistent query string for API + URL
  const generateQueryString = () => {
    const params = [];

    params.push(`page=${pagination.page}`);
    params.push(`per_page=${pagination.perPage}`);
    params.push(`sort=${sort || ""}`);

    for (const key in filters) {
      const item = filters[key];
      if (item?.selectedScope && item?.value) {
        const parsedValue = item.value
          .split(",")
          .map((v) => v.trim())
          .join("|");
        params.push(`filter[${item.selectedScope}]=${parsedValue}`);
      }
    }

    return params.join("&");
  };

  // ✅ Keep filterQuery and browser URL in sync with all dependencies
  useEffect(() => {
    if (!isHydrated) return;
    const query = generateQueryString();
    setFilterQuery(query);
    setQueryString(query); // keeps browser URL synced
  }, [pagination, filters, sort, isHydrated]);

  // ✅ Refetch on enable + query change
  const query = useQuery(
    [queryKey, filterQuery],
    () =>
      fetchFn({
        query: filterQuery,
        options: {
          headers: defaultHeaders,
        },
      }),
    {
      enabled: isHydrated && !!filterQuery && enabled,
      refetchOnWindowFocus: false,
      manual: true,

      retry: (failureCount, error) => {
        if (error?.status === 403 || error?.response?.status === 403) {
          return false;
        }

        return failureCount < 3;
      },
      onSuccess: (response) => {
        setApiResponse(response);
        setData(response.data || []);
        setPaginationInfo(response.pagination || {});
        setFilterMetadata(response.filterMetadata || {});
        setColumnMetadata(response.columnMetadata || {});

        if (getCount) getCount(response?.data?.length);
        if (externalSuccess) externalSuccess(response);
      },
      onError: (error) => {
        console.error(error);
        if (externalError) externalError(error);
      },
    }
  );

  const refetchData = () => query.refetch();

  const getFiltersObject = (data) => {
    console.log("getFiltersObject", initialFilters);

    // Clear sort if filters are empty
    if (isEmptyObject(initialFilters)) {
      console.log("isEmptyObject", data);
      setSort("");
      setFilters({});
    }

    setFilters((prevFilters) => {
      const keys = Object.keys(data);
      const changed = keys.some(
        (k) =>
          !prevFilters[k] ||
          prevFilters[k].value !== data[k].value ||
          prevFilters[k].selectedScope !== data[k].selectedScope
      );

      // ✅ If filters changed, reset to page 1
      if (changed) {
        setPagination((prev) => ({
          ...prev,
          page: !isHydrated ? initialPagination.page : 1,
        }));
        return data;
      }

      return prevFilters; // no change
    });
  };

  const getPageNumber = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleSetSort = (column) => {
    if (sort === `-${column}`) {
      setSort(column);
    } else if (sort === column) {
      setSort(`-${column}`);
    } else {
      setSort(column);
    }
  };

  const getColumns = (columnConfig) => {
    setColumns(columnConfig);
    const visible = Object.values(columnConfig).filter((col) => col.visible);
    setSortedVisibleColumns(visible.sort((a, b) => a.order - b.order));
  };

  const generateQueryStringFromState = (filters, pagination, sort) => {
    const params = [];

    params.push(`page=${pagination.page}`);
    params.push(`per_page=${pagination.perPage}`);
    params.push(`sort=${sort || ""}`);

    for (const key in filters) {
      const item = filters[key];
      if (item?.selectedScope && item?.value) {
        const parsedValue = item.value
          .split(",")
          .map((v) => v.trim())
          .join("|");
        params.push(`filter[${item.selectedScope}]=${parsedValue}`);
      }
    }

    return params.join("&");
  };

  useEffect(() => {
    if (hasParams) {
      setFilters(initialFilters);
      setSort(initialSort);

      // ✅ Delay hydration to the next event loop tick to ensure this runs BEFORE the watcher effect
      setTimeout(() => {
        setPagination(initialPagination);
        setIsHydrated(true);
      }, 0);
    } else {
      setIsHydrated(true);
    }
  }, []);

  useEffect(() => {
    console.log("filters check", filters);
  }, [filters]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasFilters = Array.from(params.keys()).some((key) => key.startsWith("filter["));
    if (!hasFilters && isHydrated && location.search) {
      const cleaned = location.search.startsWith("?") ? location.search.slice(1) : location.search;
      setFilterQuery(cleaned);
      setQueryString(cleaned);
    }
  }, [location.search, isHydrated]);

  return {
    // Core response values
    data,
    paginationInfo,
    filterMetadata,
    columnMetadata,
    apiResponse,

    // States from React Query
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetched: query.isFetched,
    isSuccess: query.isSuccess,
    isError: query.isError,
    query,

    // Internal state
    filters,
    sort,
    pagination,
    columns,
    sortedVisibleColumns,

    // Controls
    setFilters,
    setSort,
    setPagination,
    getFiltersObject,
    getPageNumber,
    handleSetSort,
    getColumns,
    refetchData,
  };
};
